import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Isvg from 'react-inlinesvg';

import { bem } from 'helpers/class-name';
import { ICONS } from 'constants/icons';
import appMessages from 'features/App/App.messages';

import { makePanelId, TOPIC_IDS, TOPIC_PANEL_TRANSITION_MS } from './HowSmartRateWorks.constants';
import AdjustsCreditsPanel from './AdjustsCreditsPanel';
import GuaranteedMinimumPanel from './GuaranteedMinimumPanel';
import MaximizesRevenuePanel from './MaximizesRevenuePanel';

import './SmartRateTopicPanel.scss';

const { block, element } = bem('smart-rate-topic-panel');

const TOPIC_PANELS = {
  [TOPIC_IDS.GUARANTEED_MINIMUM]: GuaranteedMinimumPanel,
  [TOPIC_IDS.ADJUSTS_CREDITS]: AdjustsCreditsPanel,
  [TOPIC_IDS.MAXIMIZES_REVENUE]: MaximizesRevenuePanel,
};

const SmartRateTopicPanel = ({ isOpen, labelledBy, onClose, showCloseButton, topicId }) => {
  const intl = useIntl();
  const displayedTopicIdRef = useRef(null);
  const leavingKeyRef = useRef(0);
  const [displayedTopicId, setDisplayedTopicId] = useState(null);
  const [leavingPanel, setLeavingPanel] = useState(null);

  displayedTopicIdRef.current = displayedTopicId;

  useEffect(() => {
    if (!isOpen) {
      if (!displayedTopicIdRef.current) {
        return undefined;
      }

      setLeavingPanel(null);

      const timeoutId = setTimeout(() => {
        setDisplayedTopicId(null);
      }, TOPIC_PANEL_TRANSITION_MS);

      return () => clearTimeout(timeoutId);
    }

    if (!topicId) {
      return undefined;
    }

    if (!displayedTopicIdRef.current) {
      setDisplayedTopicId(topicId);
      return undefined;
    }

    if (displayedTopicIdRef.current === topicId) {
      return undefined;
    }

    const outgoingTopicId = displayedTopicIdRef.current;
    leavingKeyRef.current += 1;

    setDisplayedTopicId(topicId);
    setLeavingPanel({ key: leavingKeyRef.current, topicId: outgoingTopicId });

    const timeoutId = setTimeout(() => {
      setLeavingPanel(null);
    }, TOPIC_PANEL_TRANSITION_MS);

    return () => clearTimeout(timeoutId);
  }, [isOpen, topicId]);

  const panelId = displayedTopicId ? makePanelId(displayedTopicId) : undefined;
  const isPanelActive = isOpen || Boolean(displayedTopicId);
  const ActiveContent = displayedTopicId ? TOPIC_PANELS[displayedTopicId] : null;
  const LeavingContent = leavingPanel ? TOPIC_PANELS[leavingPanel.topicId] : null;

  return (
    <div className={block({ open: isPanelActive })}>
      <div className={element('height-wrapper', { open: isOpen })}>
        <div className={element('height-inner')}>
          <div
            className={element('surface', { 'has-close-button': showCloseButton && isPanelActive })}
            id={panelId}
            {...(isOpen && labelledBy ? { 'aria-labelledby': labelledBy, role: 'tabpanel' } : {})}
            {...(!isOpen ? { 'aria-hidden': true } : {})}>
            {showCloseButton && isPanelActive && (
              <button
                aria-label={intl.formatMessage(appMessages.close)}
                className={element('close-button')}
                onClick={onClose}
                type="button">
                <Isvg aria-hidden src={ICONS.cross} />
              </button>
            )}
            <div className={element('content-stack')}>
              {ActiveContent && (
                <div className={element('content-layer')} key={`active-${displayedTopicId}`}>
                  <ActiveContent />
                </div>
              )}
              {LeavingContent && (
                <div
                  aria-hidden
                  className={element('content-layer', { outgoing: true })}
                  key={`leaving-${leavingPanel.key}`}>
                  <LeavingContent />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SmartRateTopicPanel.defaultProps = {
  showCloseButton: true,
};

SmartRateTopicPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  labelledBy: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  showCloseButton: PropTypes.bool,
  topicId: PropTypes.string,
};

export default SmartRateTopicPanel;
